import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import trackService from '../services/trackService';
import type { Track } from '../services/trackService';
import axios from 'axios';
import TrackForm from '../components/Tracks/TrackForm';

/**
 * Tracks page component
 */
const Tracks: React.FC = () => {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);

  useEffect(() => {
    const fetchTracks = async () => {
      try {
        setLoading(true);
        const data = await trackService.getTracks();
        setTracks(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching tracks:', err);
        if (axios.isAxiosError(err) && err.response) {
          setError(`Failed to load tracks: ${err.response.status} ${err.response.statusText}. ${err.response.data?.error || ''}`);
        } else {
          setError('Failed to load tracks. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTracks();
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleAddTrack = () => {
    setShowAddForm(true);
  };

  const handleEditTrack = (track: Track) => {
    setSelectedTrack(track);
    setShowEditForm(true);
  };

  const handleDeleteTrack = async (trackId: string) => {
    if (window.confirm('Are you sure you want to delete this track? This action cannot be undone.')) {
      try {
        await trackService.deleteTrack(trackId);
        setTracks(tracks.filter(track => track.trackId !== trackId));
      } catch (err) {
        console.error('Error deleting track:', err);
        alert('Failed to delete track. Please try again.');
      }
    }
  };

  const handleViewCourses = (trackId: string) => {
    // Navigate to track details page
    navigate(`/tracks/${trackId}`);
  };

  // Filter tracks based on search term
  const filteredTracks = tracks.filter(track => 
    track.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    track.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format currency for display
  const formatCurrency = (amount: number | string) => {
    // Ensure amount is a number
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Check if the conversion resulted in a valid number
    if (isNaN(numericAmount)) {
      return '$0.00'; // Default value if conversion fails
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numericAmount);
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Learning Tracks</h1>
      
      <div className="card">
        <div className="flex justify-between items-center mb-lg">
          <div className="flex items-center">
            <input 
              type="text" 
              placeholder="Search tracks..." 
              className="p-sm"
              value={searchTerm}
              onChange={handleSearch}
              style={{ 
                width: '250px',
                borderRadius: 'var(--border-radius-sm)'
              }}
            />
          </div>
          <button 
            className="btn btn-primary"
            onClick={handleAddTrack}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Track
          </button>
        </div>
        
        {loading ? (
          <div className="flex flex-col items-center justify-center p-lg">
            <div className="spinner"></div>
            <p className="mt-md">Loading tracks...</p>
          </div>
        ) : error ? (
          <div className="bg-error p-md" style={{ borderRadius: 'var(--border-radius-md)' }}>
            <h2 className="text-white mb-sm" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'bold' }}>Error!</h2>
            <p className="text-white">{error}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Description</th>
                  <th>Cost Price</th>
                  <th>Sales Price</th>
                  <th>Assignment Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTracks.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center p-md">
                      {searchTerm ? 'No tracks match your search criteria' : 'No tracks found. Click "Add Track" to create one.'}
                    </td>
                  </tr>
                ) : (
                  filteredTracks.map((track) => (
                    <tr key={track.trackId}>
                      <td>{track.sku}</td>
                      <td>{track.description}</td>
                      <td>{formatCurrency(track.costPrice)}</td>
                      <td>{formatCurrency(track.salesPrice)}</td>
                      <td>
                        <span 
                          className="tag"
                          style={{
                            backgroundColor: 
                              track.assignmentType === 'RANDOM' ? 'var(--color-info-light)' : 
                              track.assignmentType === 'ORDERED' ? 'var(--color-warning-light)' : 
                              'var(--color-success-light)',
                            color: 
                              track.assignmentType === 'RANDOM' ? 'var(--color-info-dark)' : 
                              track.assignmentType === 'ORDERED' ? 'var(--color-warning-dark)' : 
                              'var(--color-success-dark)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: 'bold'
                          }}
                        >
                          {track.assignmentType}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-sm">
                          {/* View Courses Button */}
                          <button 
                            className="btn btn-text"
                            onClick={() => handleViewCourses(track.trackId)}
                            title="View Courses"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                          
                          {/* Edit Track Button */}
                          <button 
                            className="btn btn-text"
                            onClick={() => handleEditTrack(track)}
                            title="Edit Track"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          
                          {/* Delete Track Button */}
                          <button 
                            className="btn btn-text"
                            onClick={() => handleDeleteTrack(track.trackId)}
                            title="Delete Track"
                            style={{ color: 'var(--color-error)' }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Add Track Form */}
      {showAddForm && (
        <div className="card mt-lg">
          <TrackForm 
            onSubmit={async (trackData) => {
              try {
                const newTrack = await trackService.createTrack(trackData as Omit<Track, 'trackId'>);
                setTracks([...tracks, newTrack]);
                setShowAddForm(false);
                alert('Track created successfully!');
              } catch (error) {
                // Error handling is done in the form component
                throw error;
              }
            }}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}
      
      {/* Edit Track Form */}
      {showEditForm && selectedTrack && (
        <div className="card mt-lg">
          <TrackForm 
            initialData={selectedTrack}
            isEditing={true}
            onSubmit={async (trackData) => {
              try {
                const updatedTrack = await trackService.updateTrack(
                  selectedTrack.trackId, 
                  trackData
                );
                
                // Update the tracks list with the updated track
                setTracks(tracks.map(track => 
                  track.trackId === selectedTrack.trackId ? updatedTrack : track
                ));
                
                setShowEditForm(false);
                setSelectedTrack(null);
                alert('Track updated successfully!');
              } catch (error) {
                // Error handling is done in the form component
                throw error;
              }
            }}
            onCancel={() => {
              setShowEditForm(false);
              setSelectedTrack(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Tracks;
